'use client';
import { useUser } from '@/hooks/auth/use-user';
import { Box, Divider, IconButton, MenuItem, Popover, Tooltip, Typography } from '@mui/material';
import { useTranslate } from '@tolgee/react';
import React, { useState } from 'react';
import SolarCopyOutline from './icons/SolarCopyOutline';
import SolarLogout3Outline from './icons/SolarLogout3Outline';

// 类型定义
interface UserMenuProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
}

/**
 * 用户菜单组件
 * 适配暗夜模式/赛博朋克风格的用户菜单
 */
export const UserMenu: React.FC<UserMenuProps> = ({ open, anchorEl, onClose }) => {
  const { user, logout } = useUser();
  const { t } = useTranslate();
  const [copyTooltip, setCopyTooltip] = useState(t('common.copy'));

  const handleCopyUsername = () => {
    if (user?.userName) {
      navigator.clipboard.writeText(user.userName);
      setCopyTooltip(t('common.copied'));
      setTimeout(() => setCopyTooltip(t('common.copy')), 2000);
    }
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{
        paper: {
          sx: {
            overflow: 'hidden',
            mt: 1,
            borderRadius: '16px',
            backgroundColor: 'rgba(21, 19, 29, 0.95)',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            backdropFilter: 'blur(20px)',
            position: 'relative',
            width: 'fit-content',
            // 赛博朋克风格的外发光和阴影
            boxShadow: `
              0 10px 40px rgba(0, 0, 0, 0.5),
              0 0 30px rgba(250, 148, 28, 0.15),
              inset 0 1px 0 rgba(255, 255, 255, 0.1)
            `,
            // 添加渐变边框效果
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              borderRadius: '16px',
              padding: '1px',
              background:
                'linear-gradient(135deg, rgba(250, 148, 28, 0.4), rgba(111, 67, 243, 0.4), rgba(250, 148, 28, 0.4))',
              mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
              maskComposite: 'xor',
              opacity: 0.6,
            },
          },
        },
      }}
      disableScrollLock
      keepMounted
    >
      {/* 用户信息区域 */}
      <Box
        sx={{
          px: 2,
          pt: 1.5,
          pb: 1,
          position: 'relative',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography
            variant="body2"
            fontWeight="medium"
            noWrap
            sx={{
              flex: 1,
              color: 'rgba(255, 255, 255, 0.9)',
              fontSize: '14px',
            }}
          >
            {user?.userName}
          </Typography>
          <Tooltip title={copyTooltip} placement="top">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleCopyUsername();
              }}
              sx={{
                p: 0.5,
                color: 'rgba(255, 255, 255, 0.6)',
                '@media (hover: hover)': {
                  '&:hover': {
                    color: 'rgba(250, 148, 28, 1)',
                    backgroundColor: 'rgba(250, 148, 28, 0.1)',
                    borderRadius: '8px',
                  },
                },
              }}
            >
              <Box component={SolarCopyOutline} width={16} height={16} style={{ color: 'inherit' }} />
            </IconButton>
          </Tooltip>
        </Box>
        <Typography
          variant="caption"
          sx={{
            color: 'rgba(255, 255, 255, 0.5)',
            fontSize: '12px',
          }}
          noWrap
        >
          {user?.email || user?.phone}
        </Typography>
      </Box>

      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)', my: 0.5 }} />

      {/* 退出登录 */}
      <MenuItem
        onClick={() => {
          onClose();
          logout();
        }}
        sx={{
          gap: 1.5,
          py: 1,
          px: 2,
          borderRadius: '12px',
          color: 'rgba(239, 68, 68, 0.9)',
          fontSize: '14px',
          fontWeight: 500,
          whiteSpace: 'nowrap',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '@media (hover: hover)': {
            '&:hover': {
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              transform: 'translateX(2px)',
              borderLeft: '2px solid rgba(239, 68, 68, 0.6)',
              boxShadow: 'inset 0 0 10px rgba(239, 68, 68, 0.1)',
            },
          },
        }}
      >
        <Box component={SolarLogout3Outline} width={20} height={20} style={{ color: 'inherit' }} />
        {t('user.action.logout')}
      </MenuItem>
    </Popover>
  );
};

export default UserMenu;
